import $inoe from "@/config";
import db from "@/db";
import api from "@/http/api";
import router from "@/router";
import { RootState } from "@/store/types";
import { Action, ActionTree } from "vuex";
import { AuthenticationState, SessionState, SESSION_STORE as s } from "./type";

import { utils } from '@/util';
import { AxiosError, AxiosResponse } from "axios";

const SESSION_TRACE = "[ANMOBI][SESSION]";
const traceSession = (step: string, data?: any) => {
    const stamp = new Date().toISOString();
    if (typeof data === "undefined") {
        console.log(`${SESSION_TRACE}[${stamp}] ${step}`);
        return;
    }
    console.log(`${SESSION_TRACE}[${stamp}] ${step}`, data);
};
const traceSessionWarn = (step: string, data?: any) => {
    const stamp = new Date().toISOString();
    if (typeof data === "undefined") {
        console.warn(`${SESSION_TRACE}[${stamp}] ${step}`);
        return;
    }
    console.warn(`${SESSION_TRACE}[${stamp}] ${step}`, data);
};
const traceSessionError = (step: string, data?: any) => {
    const stamp = new Date().toISOString();
    if (typeof data === "undefined") {
        console.error(`${SESSION_TRACE}[${stamp}] ${step}`);
        return;
    }
    console.error(`${SESSION_TRACE}[${stamp}] ${step}`, data);
};

export const register: Action<SessionState, RootState> = ({ commit, getters }, { dossier, accesskey, username, cluster }) => {
    return new Promise((resolve, reject) => {
        traceSession("register:start", {
            dossier,
            username,
            cluster,
            accesskeyLength: (accesskey || "").length
        });
        $inoe.setCluster(cluster);
        if ($inoe.getAccessKey() === accesskey
            && $inoe.getDossier() === dossier
            && $inoe.getUsername() === username) {
            console.warn("Clé identique : étape suivante")
            router.push("/session/open");
            resolve("done");
        } else {
            $inoe.setDossier(dossier.replaceAll(/\s/g, ''));
            $inoe.setAccessKey(accesskey.replaceAll(/\s/g, ''));
            $inoe.setUsername(username.replaceAll(/\s/g, ''));
            api.loginController.login($inoe.getRemoteDbURL(), $inoe.getRemoteDBUserName(), $inoe.getRemoteDBPassword())
                .then((rep: AxiosResponse) => {
                    traceSession("register:login-success", { status: rep.status, remoteDb: $inoe.getRemoteDbURL() });
                    router.push("/session/open");
                    resolve("done")
                })
                .catch((err: AxiosError) => {
                    traceSessionError("register:login-failed", {
                        message: err && err.message,
                        code: (err as any) && (err as any).code,
                        responseStatus: err && err.response && err.response.status
                    });
                    $inoe.setDossier("");
                    $inoe.setAccessKey("");
                    $inoe.setUsername("");
                    reject("Failed")
                })
        }
    })
};
export const openSession: Action<SessionState, RootState> = ({ commit, dispatch, getters, state }) => {
    return new Promise((resolve, reject) => {
        traceSession("openSession:start", {
            dossier: $inoe.getDossier(),
            username: $inoe.getUsername(),
            cluster: $inoe.getCluster(),
            remoteDb: $inoe.getRemoteDbURL(),
            online: navigator.onLine
        });
        api.dbController.initLocalDB().then(() => {
            api.dbController.getLocalDBSize().then((localSize: number) => {
                traceSession("openSession:local-size", { localSize });
                commit(s.SET_PROGRESS, localSize);
                api.loginController.login($inoe.getRemoteDbURL(), $inoe.getRemoteDBUserName(), $inoe.getRemoteDBPassword())
                    .then((rep: AxiosResponse) => {
                        traceSession("openSession:remote-login-success", { status: rep.status });
                        api.dbController.initRemoteDB($inoe.getRemoteDbURL(), $inoe.getRemoteDBUserName(), $inoe.getRemoteDBPassword())
                            .then((initRemote: any) => {
                                api.dbController.getRemoteDBSize().then((remoteSize: number) => {
                                    traceSession("openSession:remote-size", { remoteSize, localSize });
                                    let session = {
                                        status: AuthenticationState.Success
                                    }
                                    commit(s.SET_SESSION, session);
                                    commit(s.SET_MAX_DB_SIZE, remoteSize);
                                    commit(s.SET_IS_OFFLINE, false);
                                    setTimeout(function () { router.push("/session/sync"); }, 1000);
                                    resolve({});
                                })
                            })
                    })
                    .catch((err: AxiosError) => {
                        traceSessionWarn("openSession:remote-login-failed-going-offline", {
                            message: err && err.message,
                            responseStatus: err && err.response && err.response.status,
                            localSize
                        });
                        console.warn("Mobi Offline true");
                        commit(s.SET_IS_OFFLINE, true);
                        if (localSize <= 156) {
                            traceSessionWarn("openSession:offline-no-data", { localSize, threshold: 156 });
                            router.push("/erreurs/no-internet")
                            reject({})
                        } else {
                            traceSession("openSession:offline-with-local-data", { localSize });
                            api.dbController.initRemoteDB($inoe.getRemoteDbURL(), $inoe.getRemoteDBUserName(), $inoe.getRemoteDBPassword());
                            let session = {
                                status: AuthenticationState.Success
                            }
                            commit(s.SET_SESSION, session);
                            setTimeout(function () { router.push("/session/sync"); }, 1000);
                            resolve({});
                        }
                    })
            });
        }).catch((error: any) => {
            traceSessionError("openSession:init-local-failed", error);
            $inoe.setDossier("");
            $inoe.setAccessKey("");
            $inoe.setUsername("");
            router.push("/session/register");
            reject({})
        })
    });
}

export const closeSession: Action<SessionState, RootState> = ({ commit, getters, rootGetters }) => {
    return new Promise((resolve, reject) => {
        let session = getters.getSession;
        let noSession = getters.getNoSession;
        session.status = AuthenticationState.Closing;
        let etablissementId = rootGetters.getCurrentEtablissement.id;
        commit(s.SET_SESSION, session);
    })
}

export const syncConfiguration: Action<SessionState, RootState> = ({ commit, dispatch, getters, state }) => {
    traceSession("syncConfiguration:start", {
        remoteDb: $inoe.getRemoteDbURL(),
        username: $inoe.getUsername(),
        online: navigator.onLine
    });
    return new Promise((resolve, reject) => {
        api.dbController.sync({
            live: false,
            retry: false,
            doc_ids: ["users", "dateExport", "version", "infoDossier", $inoe.getUsername()||"info"]
        })
        .on("complete",(info:any)=>{
            traceSession("syncConfiguration:complete", { info });
            dispatch("doLoadConfiguration").then(() => {
                resolve("syncConfiguration done");
            }).catch((err: any) => {
                traceSessionError("syncConfiguration:doLoadConfiguration-failed", err);
                reject(err);
            });
        }) 
        .on("error",(info:any)=>{
            traceSessionError("syncConfiguration:error", info);
            dispatch("doLoadConfiguration").then(() => {
                resolve("syncConfiguration done (with error)");
            }).catch((err: any) => {
                reject(err);
            });
        })
    });
}

export const doLoadConfiguration: Action<SessionState, RootState> = ({ commit, dispatch, getters, state, rootGetters }) => {
    return new Promise((resolve, reject) => {
    const loadConfigRunId = `cfg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    traceSession("doLoadConfiguration:start", {
        loadConfigRunId,
        dossier: $inoe.getDossier(),
        username: $inoe.getUsername(),
        online: navigator.onLine
    });
    // ÉTAPE 1: Charger les docs de config depuis localDB directement
    Promise.all([
        db.localDB.get("infoDossier").catch(() => ({})),
        db.localDB.get("users").catch(() => ({})),
        db.localDB.get("dateExport").catch(() => ({}))
    ]).then(async ([infoDossier, users, dateExport]) => {
        // ÉTAPE 2: Récupérer le cluster stocké LOCALEMENT
        const previousCluster = infoDossier.cluster || "c1";
        console.log("Cluster actuellement en LOCAL DB:", previousCluster);
        
        if (!$inoe.getUsername() && rootGetters.getCurrentUser && rootGetters.getCurrentUser.login) {
            $inoe.setUsername(rootGetters.getCurrentUser.login);
        }
        
        // ÉTAPE 3: Récupérer le NOUVEAU cluster depuis l'API R (source de vérité)
        let currentCluster = previousCluster;
        let isOfflineMode = false;
        try {
            const clusterResp = await api.infoController.getClusterInfo($inoe.getDossier());
            if (clusterResp && clusterResp.data && clusterResp.data.cluster) {
                currentCluster = clusterResp.data.cluster;
                console.log("Cluster retourné par API R:", currentCluster);
            }
        } catch (e) {
            console.warn("Impossible de récupérer le cluster via l'API R (mode offline?), utilisation du local:", previousCluster);
            isOfflineMode = true;
        }
        
        // Mettre à jour le localStorage avec le nouveau cluster
        $inoe.setCluster(currentCluster);
        commit(s.SET_IS_OFFLINE, isOfflineMode);
        const shouldReinitRemote = previousCluster !== currentCluster && !isOfflineMode;
        console.log("Migration détectée?:", shouldReinitRemote, `(${previousCluster} → ${currentCluster})`);
        console.log("Mode offline:", isOfflineMode);
        console.log("Dossier:", $inoe.getDossier(), "User:", $inoe.getUsername());
        console.log("Remote DB URL cible:", $inoe.getRemoteDbURL());

        // Aussi mettre à jour la conf pour les autres composants
        $inoe.setRemoteConf({infoDossier, users, dateExport});

        // En mode offline, skip toute la partie migration/remoteDB
        if (isOfflineMode) {
            console.warn("Mode offline détecté: skip migration, passage direct à doSync");
            dispatch("doSync")
                .then(() => {
                    traceSession("doLoadConfiguration:offline-doSync-resolved", { loadConfigRunId });
                    resolve("offline-mode");
                })
                .catch((err) => {
                    traceSessionError("doLoadConfiguration:offline-doSync-failed", { loadConfigRunId, err });
                    reject(err);
                });
            return; // Early return en mode offline
        }

        // Mode ONLINE : continuer avec la logique de migration
        const reinitPromise = shouldReinitRemote
                        ? api.dbController.initRemoteDB(
                                $inoe.getRemoteDbURL(),
                                $inoe.getRemoteDBUserName(),
                                $inoe.getRemoteDBPassword()
                            )
                        : Promise.resolve();

        const ensureSystemDoc = async (docId: string, logLabel: string) => {
            if (!db.remoteDB) {
                console.warn(`${logLabel}: remoteDB indisponible, skip`);
                return;
            }

            const localDoc = await db.localDB.get(docId).catch(() => null);
            const remoteDoc = await db.remoteDB.get(docId).catch(() => null);

            if (!remoteDoc) {
                console.warn(`${logLabel}: doc serveur introuvable`);
                return;
            }

            if (!localDoc) {
                const docToCreate = { ...remoteDoc };
                delete (docToCreate as any)._rev;
                await db.localDB.put(docToCreate);
                console.log(`${logLabel} récupéré`);
                return;
            }

            if (localDoc._rev !== remoteDoc._rev) {
                const updatedDoc = {
                    ...remoteDoc,
                    _id: localDoc._id,
                    _rev: localDoc._rev
                };
                await db.localDB.put(updatedDoc);
                console.log(`${logLabel} mis à jour`);
            }
        };

        const ensureDateExportUpToDate = async () => {
            if (!db.remoteDB) {
                console.warn("dateExport: remoteDB indisponible, skip");
                return;
            }

            const localDateExport = await db.localDB.get("dateExport").catch(() => null);
            const remoteDateExport = await db.remoteDB.get("dateExport").catch(() => null);

            if (!remoteDateExport) {
                console.warn("dateExport: doc serveur introuvable");
                return;
            }

            if (!localDateExport) {
                const docToCreate = { ...remoteDateExport };
                delete (docToCreate as any)._rev;
                await db.localDB.put(docToCreate);
                console.log("dateExport récupéré:", docToCreate.lastExport);
                return;
            }

            if (localDateExport._rev !== remoteDateExport._rev) {
                const updatedDoc = {
                    ...remoteDateExport,
                    _id: localDateExport._id,
                    _rev: localDateExport._rev
                };
                await db.localDB.put(updatedDoc);
                console.log("dateExport mis à jour:", updatedDoc.lastExport);
            }
        };

        reinitPromise.then(() => {
            return ensureDateExportUpToDate()
                .then(() => ensureSystemDoc("infoDossier", "infoDossier"))
                .then(() => {
            if (shouldReinitRemote) {
                console.log("RemoteDB réinitialisée pour le cluster:", currentCluster);
                // Vérifier les pointages non envoyés (isInoe = false)
                console.log("Vérification des changements locaux à envoyer...");
                return db.localDB.allDocs({include_docs: true}).then((result: any) => {
                    const pointagesNotSent = result.rows.filter((row: any) => 
                        row.doc && row.doc.isInoe === false
                    );
                    console.log(`Pointages non envoyés (isInoe=false): ${pointagesNotSent.length}`);
                    pointagesNotSent.forEach((row: any, idx: number) => {
                        if (idx < 5) console.log(`  - ${row.doc._id}: isInoe=false`);
                    });
                    
                    if (pointagesNotSent.length > 0) {
                        console.log("Envoi des changements locaux (pointages isInoe=false) vers serveur...");
                        return new Promise((resolvePush) => {
                            let changeCount = 0;
                            api.dbController.sync({
                                live: false,
                                retry: false,
                                direction: "push" // PUSH ONLY: envoyer local → remote
                            }).on("complete", (info: any) => {
                                console.log(`PUSH complet: ${changeCount} changements envoyés`);
                                resolvePush(null);
                            }).on("change", (info: any) => {
                                changeCount += info.change && info.change.docs ? info.change.docs.length : 0;
                                console.log(`PUSH en cours: ${changeCount} docs envoyés`);
                            }).on("error", (err: any) => {
                                console.warn("Erreur PUSH (non bloquant):", err);
                                resolvePush(null);
                            });
                        });
                    } else {
                        console.log("Aucun pointage en attente (isInoe=false), pas de PUSH nécessaire");
                        return Promise.resolve();
                    }
                }).then(() => {
                    // Détruire COMPLÈTEMENT localDB pour reset tous les indexes et checkpoints
                    console.log("Destruction complète de localDB pour reset intégral...");
                    return db.localDB.destroy();
                }).then(() => {
                    // Réinitialiser localDB **vierge**
                    console.log("Réinitialisation de localDB...");
                    return api.dbController.initLocalDB();
                }).then(() => {
                    // Recharger les docs système depuis le nouveau serveur
                    console.log("Rechargement des docs système depuis le nouveau serveur...");
                    
                    // D'abord supprimer les vieux docs système pour éviter les conflits
                    const systemDocIds = ["users", "dateExport", "version", "infoDossier"];
                    return Promise.all(systemDocIds.map(docId => 
                        db.localDB.get(docId).then((doc: any) => {
                            console.log(`Suppression du vieux doc: ${docId}`);
                            return db.localDB.remove(doc);
                        }).catch(() => null) // Ignore si n'existe pas
                    )).then(() => {
                        // Faire un FULL SYNC sans restrictions pour bootstrapper la DB vide
                        console.log("FULL SYNC depuis NOUVEAU serveur (bootstrap de la DB)...");
                        return new Promise((resolveSyncConfig) => {
                            let syncCount = 0;
                            api.dbController.sync({
                                live: false,
                                retry: false
                                // NO doc_ids filter - force FULL sync to bootstrap empty DB
                            }).on("change", (info: any) => {
                                syncCount += info.change && info.change.docs ? info.change.docs.length : 0;
                                console.log(`Docs reçus: ${syncCount}`);
                            }).on("complete", () => {
                                console.log(`Tous les docs rechargés depuis nouveau serveur (${syncCount} docs total)`);
                                resolveSyncConfig(null);
                            }).on("error", (err: any) => {
                                console.error("Erreur FULL SYNC:", err);
                                resolveSyncConfig(null);
                            });
                        });
                    });
                }).then(() => {
                    // CRITIQUE: Après FULL SYNC, s'assurer qu'on a le BON dateExport du serveur
                    // (pas une version créée automatiquement en local avec une mauvaise date)
                    console.log("Vérification du dateExport après FULL SYNC...");
                    return new Promise((resolveCheck) => {
                        // Re-sync JUSTE dateExport pour s'assurer qu'on a la bonne version du serveur
                        let docReceived = false;
                        api.dbController.sync({
                            live: false,
                            retry: false,
                            doc_ids: ["dateExport"]  // Forcer récupération du doc du serveur
                        }).on("change", (info: any) => {
                            docReceived = true;
                            console.log("dateExport recevable depuis serveur");
                        }).on("complete", () => {
                            console.log(`dateExport forcément synced depuis serveur (docReceived=${docReceived})`);
                            // Vérifier qu'on a la bonne version
                            db.localDB.get("dateExport").then((doc: any) => {
                                console.log(`dateExport final en local: lastExport=${doc.lastExport}, createdAt=${doc._id}`);
                                resolveCheck(null);
                            }).catch((err: any) => {
                                console.warn("Pas de dateExport trouvé après sync (création locale):", err);
                                resolveCheck(null);
                            });
                        }).on("error", (err: any) => {
                            console.error("Erreur vérification dateExport:", err);
                            resolveCheck(null);
                        });
                    });
                }).then(() => {
                    // Vérifier qu'on n'a que les docs du nouveau cluster
                    return db.localDB.allDocs({include_docs: true}).then((result: any) => {
                        const systemDocs = result.rows.filter((row: any) => 
                            ["users", "dateExport", "version", "infoDossier"].includes(row.doc._id)
                        );
                        console.log(`Docs système en local après migration: ${systemDocs.length}`);
                        systemDocs.forEach((row: any) => {
                            console.log(`  - ${row.doc._id}: ${JSON.stringify(row.doc).substring(0, 100)}`);
                        });
                    });
                }).then(() => {
                    // Rafraîchir la date de dernière synchro depuis le nouveau dateExport
                    console.log("Rafraîchissement de la date de dernier sync...");
                    return dispatch("parametres/getLastSync", {}, { root: true });
                }).then(() => {
                    console.log("Reset complet: localDB détruite et réinitalisée. Prêt pour resync intégrale depuis nouveau cluster");
                    return continueMigration();
                }).catch((err: any) => {
                    console.error("Erreur lors du reset complet (non bloquant):", err);
                    return continueMigration();
                });
            } else {
                return continueMigration();
            }
            });
            
            function continueMigration() {
                console.log("RemoteDB user:", $inoe.getRemoteDBUserName());
                console.log("AccessKey length:", ($inoe.getRemoteDBPassword() || "").length);
                // Détection de migration serveur (cluster R vs localDB)
                return api.loginController.login($inoe.getRemoteDbURL(), $inoe.getRemoteDBUserName(), $inoe.getRemoteDBPassword())
                .then(() => {
                    return dispatch("parametres/detectServerMigration", { currentCluster }, { root: true })
                    .then((result: any) => {
                        if (result.migrationDetected) {
                            console.log(`Migration serveur détectée: cluster ${result.previousCluster} → ${result.currentCluster}`);
                            // Forcer une resync complète
                            return dispatch("parametres/forceCompleteResync", { currentCluster }, { root: true })
                                .then(() => {
                                    return dispatch("doSync")
                                        .then(() => {
                                            traceSession("doLoadConfiguration:resolved", { loadConfigRunId, reason: "migration-resynced" });
                                            resolve("migration-resynced");
                                        });
                                });
                        } else {
                            return dispatch("doSync")
                                .then(() => {
                                    traceSession("doLoadConfiguration:resolved", { loadConfigRunId, reason: "no-migration" });
                                    resolve("no-migration");
                                });
                        }
                    }).catch((err) => {
                        traceSessionError("doLoadConfiguration:detectServerMigration-failed", { loadConfigRunId, err });
                        return dispatch("doSync")
                            .then(() => {
                                traceSessionWarn("doLoadConfiguration:resolved-with-detection-error", { loadConfigRunId });
                                resolve("detection-error");
                            })
                            .catch((syncErr) => {
                                reject(syncErr);
                            });
                    });
                })
                .catch((err) => {
                    traceSessionWarn("doLoadConfiguration:couch-login-failed", { loadConfigRunId, err });
                    // En mode hors ligne, continuer avec doSync si on a des données locales
                    return dispatch("doSync")
                        .then(() => {
                            traceSession("doLoadConfiguration:resolved", { loadConfigRunId, reason: "offline-mode-no-auth" });
                            resolve("offline-mode-no-auth");
                        })
                        .catch((syncErr: any) => {
                            reject(syncErr);
                        });
                });
            }
        }).catch((err: any) => {
            traceSessionWarn("doLoadConfiguration:remote-reinit-failed", { loadConfigRunId, err });
            return dispatch("doSync")
                .then(() => {
                    traceSessionWarn("doLoadConfiguration:resolved", { loadConfigRunId, reason: "reinit-remote-error" });
                    resolve("reinit-remote-error");
                })
                .catch((syncErr) => {
                    reject(syncErr);
                });
        });
    }).catch(e=>{
        traceSessionError("doLoadConfiguration:unhandled-error", { loadConfigRunId, e });
        alert("Erreur de chargement de la configuration, vérifier votre connexion internet")
        reject(e);
    })
    });
}

export const reloadConfiguration: Action<SessionState, RootState> = ({ commit, dispatch, getters, state }) => {
    return new Promise((resolve, reject) => {
        api.dbController.sync({
            live: false,
            retry: false,
            doc_ids: ["users", "dateExport", "version", "infoDossier"]
        }).on("paused", (info: any) => {
            resolve("done");
        }).on("change", (info: any) => {
            console.log("SyncConfig change", info);
            resolve("done");
        })
    })
    
}
export const sync: Action<SessionState, RootState> = ({ commit, dispatch, getters, state, rootGetters }) => {
    commit(s.SET_LOCALDB_STATUS, "starting");
    commit(s.SET_LOCALDB_COMPLETE, false);
    commit(s.SET_MEMDB_STATUS, "empty");

    commit(s.SET_LOCALDB_STATUS, "configured")
    return dispatch("syncConfiguration");
}
export const doSync: Action<SessionState, RootState> = ({ commit, dispatch, getters, state, rootGetters }) => {
    return new Promise((resolve, reject) => {
        const syncRunId = `sync-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        traceSession("doSync:start", {
            syncRunId,
            online: navigator.onLine,
            currentUser: rootGetters.getCurrentUser && rootGetters.getCurrentUser.login,
            remoteDb: $inoe.getRemoteDbURL()
        });
        const startLiveSyncIfOnline = () => {
            if (state.isOffline || !navigator.onLine) {
                traceSessionWarn("doSync:skip-livesync-offline", {
                    syncRunId,
                    isOfflineState: state.isOffline,
                    online: navigator.onLine
                });
                return;
            }
            dispatch("livesync");
        };
        const resolveOfflineWithoutRemoteSync = () => {
            traceSessionWarn("doSync:offline-local-only", {
                syncRunId,
                isOfflineState: state.isOffline,
                online: navigator.onLine
            });
            commit(s.SET_LOCALDB_COMPLETE, true);
            commit(s.SET_LOCALDB_STATUS, "complete");
            commit(s.SET_START_TIME, {});
            dispatch("loadMemDB");
            traceSession("doSync:resolved", { syncRunId, reason: "offline-local-only" });
            resolve("offline-local-only");
        };
        const startReplication = () => {
            if (state.isOffline || !navigator.onLine) {
                resolveOfflineWithoutRemoteSync();
                return;
            }
            traceSession("doSync:startReplication", { syncRunId });
            db.sync(
                {
                    batch_size: 5000,
                    batches_limit: 1,
                    live: false,
                    retry: false
                }
            )
                .on('complete', function () {
                    traceSession("doSync:complete-event", { syncRunId });
                    db.remoteDB.info().then((info: any) => {
                        console.warn("RemoteDB docs:", info.doc_count);
                    }).catch(() => {
                        console.warn("RemoteDB info indisponible");
                    });
                    db.localDB.info().then((info: any) => {
                        console.warn("LocalDB docs:", info.doc_count);
                    }).catch(() => {
                        console.warn("LocalDB info indisponible");
                    });
                    commit(s.SET_LOCALDB_COMPLETE, true);
                    commit(s.SET_LOCALDB_STATUS, "complete");
                    commit(s.SET_START_TIME, {});
                    console.warn("Replication complete at " + utils.getCurrentTimeNow().format("HH:mm:ss"))

                    dispatch("loadMemDB");
                    startLiveSyncIfOnline();
                    traceSession("doSync:resolved", { syncRunId, reason: "sync-complete" });
                    resolve("sync-complete"); // ← RÉSOUDRE LA PROMISE ICI
                })
                .on("paused", (info: any) => {
                    traceSessionWarn("doSync:paused-event", {
                        syncRunId,
                        status: info && info.result && info.result.status,
                        ok: info && info.result && info.result.ok,
                        lastSeq: info && info.result && info.result.last_seq,
                        docsRead: info && info.result && info.result.docs_read,
                        docsWritten: info && info.result && info.result.docs_written,
                        online: navigator.onLine
                    });
                    console.log("Replication paused ...", info)
                    commit(s.SET_LOCALDB_COMPLETE, true);
                    commit(s.SET_LOCALDB_STATUS, "complete");
                    // En offline ou quand données à jour, "paused" est une fin valide
                    db.remoteDB.info().then((info: any) => {
                        console.warn("RemoteDB docs:", info.doc_count);
                    }).catch(() => {
                        console.warn("RemoteDB info indisponible");
                    });
                    db.localDB.info().then((info: any) => {
                        console.warn("LocalDB docs:", info.doc_count);
                    }).catch(() => {
                        console.warn("LocalDB info indisponible");
                    });
                    // Traiter "paused" comme une fin valide (comme reloadConfiguration)
                    dispatch("loadMemDB");
                    startLiveSyncIfOnline();
                    traceSession("doSync:resolved", { syncRunId, reason: "sync-paused-valid" });
                    resolve("sync-paused-valid"); // ← RÉSOUDRE ICI AUSSI
                }).on("change", (info: any) => {
                    traceSession("doSync:change-event", {
                        syncRunId,
                        direction: info && info.direction,
                        docs: info && info.change && info.change.docs ? info.change.docs.length : 0
                    });
                    console.warn("Replication change:", info.direction, "docs:", info.change && info.change.docs ? info.change.docs.length : 0);
                    db.localDB.info().then((info: any) => {
                        db.localDBSize = info.doc_count;
                        commit(s.SET_PROGRESS, db.localDBSize);
                        commit(s.SET_START_TIME, utils.getCurrentTimeNow());
                    })
                }).on('active', function (info: any) {
                    traceSession("doSync:active-event", { syncRunId });
                    commit(s.SET_LOCALDB_STATUS, "change");
                    console.warn("Replication active at " + utils.getCurrentTimeNow().format("HH:mm:ss"))
                }).on('error', function (err: any) {
                    traceSessionError("doSync:error-event", {
                        syncRunId,
                        err,
                        localDBSize: db.localDBSize,
                        maxDbSize: getters.getMaxDbSize,
                        online: navigator.onLine
                    });
                    console.warn("Mode Offline", db.localDBSize)
                    console.warn("Replication error:", err && err.status ? err.status : err);
                    if (db.localDBSize >= getters.getMaxDbSize) 
                    {
                        commit(s.SET_LOCALDB_COMPLETE, true);
                        commit(s.SET_LOCALDB_STATUS, "complete");
                        dispatch("loadMemDB");
                        startLiveSyncIfOnline();
                        traceSessionWarn("doSync:resolved-with-fallback", { syncRunId });
                        resolve("sync-complete-with-fallback");
                    } else {
                        console.log("size wrong: ", db.localDBSize, getters.getMaxDbSize)
                        console.error(err);
                        reject(new Error("Replication failed: insufficient local data"));
                    }
                });
        };

        try {
            if (!rootGetters.getCurrentUser || typeof rootGetters.getCurrentUser.login === 'undefined') {
                const username = $inoe.getUsername();
                traceSessionWarn("doSync:missing-current-user", { syncRunId, username });
                if (username) {
                    db.localDB.get("users").then((usersDoc: any) => {
                        const foundUser = usersDoc.list && usersDoc.list.find((u: any) => u.login === username);
                        if (foundUser) {
                            traceSession("doSync:user-found-in-localdb", { syncRunId, username });
                            dispatch("moncompte/selectCurrentUser", foundUser, { root: true });
                            startReplication();
                        } else {
                            console.log("Login required ...", rootGetters.getCurrentUser)
                            commit(s.SET_LOCALDB_STATUS, "configrequired")
                            reject(new Error("User not found in local DB"));
                        }
                    }).catch((err: any) => {
                        console.log("Login required ...", rootGetters.getCurrentUser)
                        commit(s.SET_LOCALDB_STATUS, "configrequired")
                        reject(err);
                    });
                    return;
                }
                console.log("Login required - no username ...")
                commit(s.SET_LOCALDB_STATUS, "configrequired")
                reject(new Error("No username available"));
            } else {
                startReplication();
            }
        } catch (e) {
            traceSessionError("doSync:catch", { syncRunId, e });
            reject(e);
        }
    });
}
export const skipSync: Action<SessionState, RootState> = ({ commit, dispatch, getters, state, rootGetters }) => {
    commit(s.SET_LOCALDB_COMPLETE, true);
    commit(s.SET_LOCALDB_STATUS, "complete");
    commit(s.SET_START_TIME, {});
    console.warn("skipeSync  at " + utils.getCurrentTimeNow().format("HH:mm:ss"))

    dispatch("loadMemDB");
    if (!state.isOffline && navigator.onLine) {
        dispatch("livesync");
    } else {
        traceSessionWarn("skipSync:skip-livesync-offline", {
            isOfflineState: state.isOffline,
            online: navigator.onLine
        });
    }
}
export const livesync: Action<SessionState, RootState> = ({ commit, dispatch, getters, state, rootGetters }) => {
    const liveSyncRunId = `livesync-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    traceSession("livesync:start", { liveSyncRunId, online: navigator.onLine });
    if (state.isOffline || !navigator.onLine) {
        traceSessionWarn("livesync:skipped-offline", {
            liveSyncRunId,
            isOfflineState: state.isOffline,
            online: navigator.onLine
        });
        return;
    }
    commit(s.SET_LOCALDB_STATUS, "synclive");
    let syncOptions = {
        live: true,
        retry: true
    }
    db.sync(
        syncOptions
    )
        .on("change", (info: any) => {
            traceSession("livesync:change-event", {
                liveSyncRunId,
                direction: info && info.direction,
                docs: info && info.change && info.change.docs ? info.change.docs.length : 0
            });
            console.log('Telechargement des donnes ...', info);
            commit(s.SET_LOCALDB_STATUS, "change");
            if (info.direction === 'pull') {
                //Reception
                if (info.change.docs.length > 0 && info.change.docs.some((doc: any) => doc._id === "dateExport")) {
                    commit(s.SET_STORE_UPDATED, true);
                    // Mettre à jour lastSync immédiatement quand dateExport change
                    dispatch("parametres/getLastSync", {}, { root: true }).catch((err: any) => {
                        console.warn("Erreur mise à jour lastSync:", err);
                    });
                }
            }
            dispatch("dbChanged", info)
        }).on("paused", (info: any) => {
            traceSessionWarn("livesync:paused-event", { liveSyncRunId, online: navigator.onLine, info });
            console.warn("LiveSync paused ...");
            commit(s.SET_LOCALDB_STATUS, "paused");
            if (state.storeWillUpdate) {
                console.warn("Update MemDB")
                commit(s.SET_STORE_UPDATED, false);
            }
        }).on('active', function (info: any) {
            traceSession("livesync:active-event", { liveSyncRunId });
            console.warn("LiveSync active at " + utils.getCurrentTimeNow().format("HH:mm:ss"));
            commit(s.SET_LOCALDB_STATUS, "change");
        }).on('error', function (err: any) {
            traceSessionError("livesync:error-event", { liveSyncRunId, err });
            console.error(err);
        });

}
export const loadMemDB: Action<SessionState, RootState> = ({ commit, dispatch, getters, state, rootGetters }) => {
    traceSession("loadMemDB:entry", { memDBStatus: state.memDBStatus });
    if (state.memDBStatus === "empty") {
        console.warn("Chargement de la base mémoire...")
        commit(s.SET_MEMDB_STATUS, "loading");
        db.load().then((docs: any) => {
            console.warn("Chargement terminé at " + utils.getCurrentTimeNow().format("HH:mm:ss"))
            commit(s.SET_MEMDB_STATUS, "loaded");
            if (typeof docs.ActiviteDocument === 'undefined' || !Array.isArray(docs.ActiviteDocument) || docs.ActiviteDocument.length === 0) {
                commit(s.SET_MEMDB_STATUS, "error");
            }
        }).catch((e: any) => {
            console.error(e)
            alert("MemDB ERROR")
            commit(s.SET_MEMDB_STATUS, "error");
        });
    }
}

export const dbChanged: Action<SessionState, RootState> = ({ commit, dispatch, getters, state, rootGetters }, info) => {
    if (state.memDBStatus === "loaded")
        db.loadChanges(info.change.docs);
}



export const actions: ActionTree<SessionState, RootState> = {
    register,
    syncConfiguration,
    doLoadConfiguration,
    livesync,
    openSession,
    closeSession,
    sync,
    doSync,
    skipSync,
    loadMemDB,
    dbChanged,
    reloadConfiguration

}